import { useCallback, useEffect, useImperativeHandle, useRef, type PointerEvent } from 'react';

import { surfaces } from '@/theme/tokens';

import { toBoundedDataUrl } from './signatureImage';

/** Drawn at this multiple of the CSS size so strokes stay smooth while drawing.
 *  This is the ON-SCREEN backing store only — the exported PNG is normalised to
 *  a fixed width by `toBoundedDataUrl`, because the API column caps the data
 *  URL length and device pixel ratio must not leak into what we send. */
const EXPORT_SCALE = 2;
/** Canvas takes a literal colour — it cannot read the CSS variables the rest
 *  of the app styles from — so it is taken from the token rather than copied. */
const STROKE_COLOUR = surfaces.text.primary;
/** In CSS pixels. Scaled with the backing store, so it looks the same at any DPR. */
const STROKE_WIDTH = 2.2;

export interface SignatureCanvasHandle {
  clear: () => void;
  /** `null` when nothing has been drawn — the caller uses this to block submit. */
  toDataUrl: () => string | null;
  isEmpty: () => boolean;
}

interface SignatureCanvasProps {
  ref: React.Ref<SignatureCanvasHandle>;
  /** Notified on the first stroke and on clear, so the dialog can enable Save. */
  onDrawnChange: (hasDrawn: boolean) => void;
  ariaLabel: string;
}

/**
 * A pointer-driven drawing surface for a handwritten signature.
 *
 * Written against Pointer Events rather than a canvas library: the old portal
 * pulled in `react-canvas-draw` for this, which brings its own React tree,
 * its own ref shape, and a `toDataURL` reached by indexing into DOM children.
 * The behaviour that actually matters here is a line between two points.
 *
 * The backing store is sized to `EXPORT_SCALE` and the context scaled to match,
 * so coordinates stay in CSS pixels while strokes stay smooth. Export
 * resolution is a separate concern handled by `toBoundedDataUrl`.
 */
export const SignatureCanvas = ({ ref, onDrawnChange, ariaLabel }: SignatureCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const hasDrawn = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);

  const context = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return null;
    return canvas.getContext('2d');
  }, []);

  /** Sizes the backing store to the laid-out box. Re-run on resize because a
   *  phone rotating changes the CSS width and would otherwise stretch strokes. */
  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = context();
    if (canvas === null || ctx === null) return;

    const bounds = canvas.getBoundingClientRect();
    if (bounds.width === 0 || bounds.height === 0) return;

    // Deliberately NOT multiplied by devicePixelRatio: on a 3x phone that
    // squared the pixel count and pushed the exported PNG past the column
    // limit. Export resolution is decided at export time instead.
    const ratio = EXPORT_SCALE;
    canvas.width = Math.round(bounds.width * ratio);
    canvas.height = Math.round(bounds.height * ratio);

    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = STROKE_COLOUR;
    ctx.lineWidth = STROKE_WIDTH;
  }, [context]);

  // Held in a ref so the setup effect below does not depend on the caller's
  // callback identity. `resize()` reassigns canvas.width, which wipes the
  // backing store, so an effect that re-runs erases the signature — and it
  // would re-run on the very first stroke, because notifying the parent
  // re-renders it and an inline handler arrives with a new identity each time.
  const onDrawnChangeRef = useRef(onDrawnChange);
  useEffect(() => {
    onDrawnChangeRef.current = onDrawnChange;
  }, [onDrawnChange]);

  useEffect(() => {
    resize();

    // Resizing clears the backing store, so anything already drawn is lost. A
    // director rotating mid-signature has to start that stroke again; keeping a
    // stroke log to replay would be the fix if that ever proves annoying.
    const observer = new ResizeObserver(() => {
      const wasDrawn = hasDrawn.current;
      resize();
      if (wasDrawn) {
        hasDrawn.current = false;
        onDrawnChangeRef.current(false);
      }
    });

    const canvas = canvasRef.current;
    if (canvas !== null) observer.observe(canvas);

    return () => {
      observer.disconnect();
    };
  }, [resize]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = context();
    if (canvas === null || ctx === null) return;

    // Reset the transform so the clear covers the whole backing store, then put
    // the drawing transform back.
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();

    hasDrawn.current = false;
    onDrawnChangeRef.current(false);
  }, [context]);

  useImperativeHandle(
    ref,
    () => ({
      clear,
      isEmpty: () => !hasDrawn.current,
      toDataUrl: () => {
        const canvas = canvasRef.current;
        if (canvas === null || !hasDrawn.current) return null;
        return toBoundedDataUrl(canvas);
      },
    }),
    [clear],
  );

  const pointFrom = (event: PointerEvent<HTMLCanvasElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
  };

  const handlePointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    isDrawing.current = true;
    lastPoint.current = pointFrom(event);

    // A tap with no movement is a full stop, and a signature can legitimately
    // contain one, so mark the canvas dirty here rather than on first move.
    const ctx = context();
    const point = lastPoint.current;
    if (ctx !== null) {
      ctx.beginPath();
      ctx.arc(point.x, point.y, STROKE_WIDTH / 2, 0, Math.PI * 2);
      ctx.fillStyle = STROKE_COLOUR;
      ctx.fill();
    }

    if (!hasDrawn.current) {
      hasDrawn.current = true;
      onDrawnChangeRef.current(true);
    }
  };

  const handlePointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing.current) return;

    const ctx = context();
    const from = lastPoint.current;
    if (ctx === null || from === null) return;

    const to = pointFrom(event);

    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();

    lastPoint.current = to;
  };

  const handlePointerUp = () => {
    isDrawing.current = false;
    lastPoint.current = null;
  };

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={ariaLabel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onPointerCancel={handlePointerUp}
      // `touch-none` stops the browser scrolling the dialog while a finger draws.
      className="h-40 w-full touch-none rounded-lg border border-dashed border-divider bg-surface"
    />
  );
};
